import { useRef, useEffect, useState, CSSProperties } from 'react';
import { motion } from 'framer-motion';

const Rules = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const [isMediumScreen, setIsMediumScreen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;
    
    // Verificar tamanho da tela inicialmente
    const checkScreenSize = () => {
      const mobileView = window.innerWidth < 768;
      setIsLargeScreen(window.innerWidth >= 1280);
      setIsMediumScreen(window.innerWidth >= 768 && window.innerWidth < 1280);
      setIsMobile(mobileView);
      
      // Se for mobile, mostrar tudo imediatamente
      if (mobileView) {
        setIsInView(true);
      }
    };
    checkScreenSize();
    
    // Adicionar listener para redimensionamento
    window.addEventListener('resize', checkScreenSize);

    // Configurar o observer para detectar quando o componente está visível
    let observer: IntersectionObserver | null = null;
    
    // Pequeno atraso para evitar problemas de timing
    const timer = setTimeout(() => {
      if (currentRef && !isMobile) {
        // Definir threshold para desktop
        const threshold = 0.3;
        
        observer = new IntersectionObserver(
          (entries) => {
            const [entry] = entries;
            setIsInView(entry.isIntersecting);
          },
          { 
            threshold: threshold,
            rootMargin: '0px'
          }
        );
        
        observer.observe(currentRef);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (observer) {
        observer.disconnect();
      }
      window.removeEventListener('resize', checkScreenSize);
    };
  }, [isMobile]);

  useEffect(() => {
    // Adicionar o estilo para o efeito hover diretamente no DOM
    const style = document.createElement('style');
    style.textContent = `
      .rule-card:hover .gradient-line {
        transform: scaleX(1) !important;
      }
      .rule-card:hover h3 {
        color: #1e40af !important;
      }
      .rule-card:hover p {
        color: #374151 !important;
      }
      .rule-card:hover .rule-number {
        background-color: #bfdbfe !important;
        color: #1e3a8a !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const rules = [
    {
      number: "1",
      title: "Forme uma dupla",
      description: "Escolha um colega da mesma TURMA para formar a sua dupla."
    },
    {
      number: "2",
      title: "Defina o nome da equipe",
      description: "Definam juntos um nome criativo e original para a equipe."
    },
    {
      number: "3",
      title: "Inscrição",
      description: "A inscrição é única por Equipe. Não deixe de ler o Regulamento disponibilizado no final do site. Caso tenha dificuldade solicite o auxílio do seu professor HAPPY."
    },
    {
      number: "4",
      title: "Atenção aos prazos",
      description: "Fique atento aos prazos de cada etapa do Hackathon, pois se perder o prazo de envio de alguma etapa a equipe será desclassificada."
    }
  ];

  const sectionStyle: CSSProperties = {
    padding: '1.5rem 0 5rem',
    backgroundColor: 'white',
    overflow: 'visible',
  };

  const containerStyle: CSSProperties = {
    width: '100%',
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '0 1.5rem',
    overflow: 'visible',
    boxSizing: 'border-box',
  };

  const innerContainerStyle: CSSProperties = {
    maxWidth: '100%',
    margin: '0 auto',
    overflow: 'visible',
  };

  const textCenterStyle: CSSProperties = {
    textAlign: 'center',
    marginBottom: '4rem',
  };

  const headingStyle: CSSProperties = {
    fontSize: '2.25rem',
    fontWeight: 'bold',
    color: '#1e3a8a',
    marginBottom: '1rem',
    fontFamily: 'Kobe, sans-serif'
  };

  const dividerStyle: CSSProperties = {
    height: '0.25rem',
    width: '5rem',
    backgroundColor: '#ffb03f',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginBottom: '2rem',
  };

  const descriptionStyle: CSSProperties = {
    fontSize: '1.25rem',
    color: '#4b5563',
    maxWidth: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
  };

  const gridContainerStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: isLargeScreen 
      ? 'repeat(4, 1fr)' 
      : (isMediumScreen 
          ? 'repeat(2, 1fr)' 
          : 'repeat(1, 1fr)'),
    gap: '1.5rem',
    overflow: 'visible',
  };

  const cardStyle: CSSProperties = {
    backgroundColor: '#f9fafb',
    borderRadius: '0.75rem',
    padding: '1.5rem',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    display: 'flex',
    flexDirection: 'column',
    height: 'auto',
    minHeight: '200px',
    position: 'relative',
    overflow: 'visible',
    transition: 'all 0.3s ease',
    border: '1px solid #e5e7eb',
    cursor: 'pointer',
  };

  const ruleNumberStyle: CSSProperties = {
    backgroundColor: '#e1effe',
    color: '#1e40af',
    borderRadius: '9999px',
    width: '3rem',
    height: '3rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '1.25rem',
    marginBottom: '1rem',
    transition: 'all 0.3s ease',
  };

  const ruleTitleStyle: CSSProperties = {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#1e3a8a',
    marginBottom: '0.5rem',
    transition: 'color 0.3s ease',
  };

  const ruleDescriptionStyle: CSSProperties = {
    color: '#6b7280',
    fontSize: '1rem',
    lineHeight: '1.5',
    transition: 'color 0.3s ease',
    overflow: 'hidden',
  };

  const gradientLineStyle: CSSProperties = {
    height: '0.25rem',
    width: '100%',
    background: 'linear-gradient(to right, #60a5fa, #ffb03f, #60a5fa)',
    transform: 'scaleX(0)',
    transformOrigin: 'left',
    transition: 'transform 0.3s ease',
    marginTop: 'auto',
  };

  const titleUnderlineStyle: CSSProperties = {
    height: '0.25rem',
    width: '5rem',
    backgroundColor: '#ffb03f',
    margin: '0.5rem auto 3rem auto',
  };

  return (
    <section id="rules" style={sectionStyle} ref={ref}>
      <div style={containerStyle}>
        <div style={innerContainerStyle}>
          <motion.div
            initial={isMobile ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: isMobile ? 0 : 0.5 }}
            style={textCenterStyle}
          >
            <h2 style={headingStyle} className="title-font">Regras do Hackathon</h2>
            <div style={dividerStyle}></div>
            <p style={descriptionStyle}>
              Para participar do Hackathon Happy Escolas 2025, siga estas regras importantes:
            </p>
          </motion.div>
          
          <div style={gridContainerStyle}>
            {rules.map((rule, index) => (
              <motion.div
                key={index}
                className="rule-card"
                initial={isMobile ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ 
                  duration: isMobile ? 0 : 0.5, 
                  delay: isMobile ? 0 : index * 0.1,
                }}
                style={cardStyle}
                whileHover={isMobile ? {} : { 
                  y: -10,
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                }}
              >
                <div style={ruleNumberStyle} className="rule-number">{rule.number}</div>
                <div style={{ flex: 1, marginBottom: '0.75rem' }}>
                  <h3 style={ruleTitleStyle}>{rule.title}</h3>
                  <p style={ruleDescriptionStyle}>{rule.description}</p>
                </div>
                
                <div 
                  style={gradientLineStyle}
                  className="gradient-line"
                ></div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Rules; 